import React from "react"
import { graphql } from "gatsby"
import { H4, H5 } from "@fastly/consistently"
import MDXWrapper from "~templates/mdx"

import Fiddle from "~components/fiddle/fiddle"
import Link from "~components/link/link"
import SyntaxGuide from "~components/syntax-guide/syntax-guide"
import LabelList from "~components/label-list/label-list"

import * as styles from "./vcl-token.module.css"

const accessDescr = {
  RW: (
    <>
      can be read and <code>set</code>, but not <code>unset</code>
    </>
  ),
  RO: <>read-only</>,
  C: <>read-only, constant</>,
  RU: (
    <>
      can be read and <code>unset</code>, but not <code>set</code>
    </>
  ),
  RWU: (
    <>
      can be read, <code>set</code> and <code>unset</code>
    </>
  ),
  WO: (
    <>
      can only be <code>set</code> (cannot be read or <code>unset</code>)
    </>
  ),
}

const Template = ({ data, title, deprecated, tokenType, access, type, subroutines, args, noSyntaxGuide, children }) => {
  const hasExamples = Boolean(title && data.examples.nodes.length)
  const availableAll = subroutines && subroutines.length === 1 && subroutines[0] === "all"

  const availabilityContent = availableAll ? (
    <>
      <Link to="/reference/vcl/subroutines">all subroutines</Link>.
    </>
  ) : (
    subroutines && <LabelList labels={subroutines} linkPrefix="/reference/vcl/subroutines/" background />
  )
  const deprecatedContent = deprecated ? <span>Deprecated. </span> : ""

  return (
    <>
      {!noSyntaxGuide && (tokenType === "function" || tokenType === "statement") ? (
        <SyntaxGuide tokenType={tokenType} returnType={type} args={args} name={title} />
      ) : (
        tokenType === "variable" && (
          <p>
            <Link to={`/reference/vcl/types/${type.toLowerCase()}`}>{type}</Link>, {accessDescr[access] || ""}.
          </p>
        )
      )}

      {Boolean(deprecated) && <LabelList background float="right" labels={["Deprecated"]} />}

      <p className={styles.withLabels}>
        {deprecatedContent}
        <span>Available in</span>
        {availabilityContent}
      </p>

      {children}

      {hasExamples && (
        <>
          <H4 as="h2">
            <Link className="anchor" to="#try-it-out" id="try-it-out" />
            Try it out
          </H4>
          <p>
            <code>{title}</code> is used in the following code examples. Examples apply VCL to real-world use cases and
            can be deployed as they are, or adapted for your own service. See the{" "}
            <Link to="/solutions/examples">full list of code examples</Link> for more inspiration.
          </p>
          <p>
            Click RUN on a sample below to provision a Fastly service, execute the code on Fastly, and see how the
            function behaves.
          </p>
          {data.examples.nodes.map((eg) => (
            <div key={eg.implementations.vcl.fiddleID}>
              <H5 as="h2">{eg.title}</H5>
              <p>{eg.summary}</p>
              <Fiddle id={eg.implementations.vcl.fiddleID} />
            </div>
          ))}
        </>
      )}
    </>
  )
}

export const query = graphql`
  query ($mdxPath: String, $title: String) {
    mdx(fileAbsolutePath: { eq: $mdxPath }) {
      body
      tableOfContents(maxDepth: 10)
    }
    examples: allExamplesJson(filter: { implementations: { vcl: { tokens: { eq: $title } } } }) {
      nodes {
        title
        summary
        implementations {
          vcl {
            fiddleID
          }
        }
      }
    }
  }
`

const Wrapper = (props) =>
  MDXWrapper({
    ...props,
    Template,
    pageContext: { ...props.pageContext, withComments: true },
  })

export default Wrapper
